import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { toast } from "sonner";
import {
  Phone,
  PhoneCall,
  Loader2,
  X,
  Volume2,
  Clock,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { getSupabaseClient } from "../providers/supabase/supabase";
import type { CallStatus } from "../types";

export interface ActiveDotusCall {
  id: string; // callUuid
  gatewayCallId?: string;
  dealId: number | string;
  phone: string;
  contactName?: string;
  status: CallStatus;
  result?: string;
  durationSeconds: number;
  errorMessage?: string;
  startedAt: number;
  connectedAt?: number;
}

interface StartCallParams {
  dealId: number | string;
  phone: string;
  contactName?: string;
  salesId?: number | string;
}

interface DotusWaCallContextType {
  activeCall: ActiveDotusCall | null;
  isCalling: boolean;
  startCall: (params: StartCallParams) => Promise<boolean>;
  dismissCall: () => void;
  currentUserInstanceId: string | null;
}

const DotusWaCallContext = createContext<DotusWaCallContextType | undefined>(
  undefined,
);

// eslint-disable-next-line react-refresh/only-export-components
export const useDotusWaCall = () => {
  const context = useContext(DotusWaCallContext);
  if (!context) {
    throw new Error(
      "useDotusWaCall deve ser usado dentro de um DotusWaCallProvider",
    );
  }
  return context;
};

export const DotusWaCallProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [activeCall, setActiveCall] = useState<ActiveDotusCall | null>(null);
  const [currentUserInstanceId, setCurrentUserInstanceId] = useState<
    string | null
  >(null);

  const pollIntervalRef = useRef<any>(null);
  const timerIntervalRef = useRef<any>(null);
  const autoCloseTimeoutRef = useRef<any>(null);

  // Busca a instância do WhatsApp do usuário logado diretamente via Supabase Auth
  useEffect(() => {
    let isMounted = true;
    const supabase = getSupabaseClient();

    const fetchInstanceForUser = async (userId: string, email?: string) => {
      try {
        let query = supabase.from("sales").select("id, dotus_instance_id");
        if (userId && email) {
          query = query.or(`user_id.eq.${userId},email.eq.${email}`);
        } else if (userId) {
          query = query.eq("user_id", userId);
        } else if (email) {
          query = query.eq("email", email);
        }

        const { data } = await query.maybeSingle();
        if (isMounted && data?.dotus_instance_id) {
          setCurrentUserInstanceId(data.dotus_instance_id);
        }
      } catch (err) {
        console.warn(
          "[DotusWaCall] Erro ao carregar dotus_instance_id do operador:",
          err,
        );
      }
    };

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user?.id) {
        fetchInstanceForUser(session.user.id, session.user.email);
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user?.id) {
        fetchInstanceForUser(session.user.id, session.user.email);
      } else if (isMounted) {
        setCurrentUserInstanceId(null);
      }
    });

    return () => {
      isMounted = false;
      subscription?.unsubscribe();
    };
  }, []);

  // Cronômetro para o status CONNECTED
  useEffect(() => {
    if (activeCall?.status === "CONNECTED") {
      if (!timerIntervalRef.current) {
        timerIntervalRef.current = setInterval(() => {
          setActiveCall((prev) => {
            if (!prev || prev.status !== "CONNECTED") return prev;
            return {
              ...prev,
              durationSeconds: prev.durationSeconds + 1,
            };
          });
        }, 1000);
      }
    } else {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    }

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [activeCall?.status]);

  // Polling de sincronização de status em tempo real com o backend
  useEffect(() => {
    if (
      !activeCall ||
      activeCall.status === "ENDED" ||
      activeCall.status === "FAILED"
    ) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      return;
    }

    const checkStatus = async () => {
      try {
        const supabase = getSupabaseClient();
        const {
          data: { session },
        } = await supabase.auth.getSession();
        const token = session?.access_token;

        const headers: Record<string, string> = {
          "Content-Type": "application/json",
        };
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        }

        const res = await fetch(
          `/api/calls/dotus/status?id=${encodeURIComponent(activeCall.id)}`,
          {
            headers,
            credentials: "include",
          },
        );

        if (!res.ok) return;
        const json = await res.json();
        const callData = json.call;

        if (callData?.status && callData.status !== activeCall.status) {
          const newStatus = callData.status as CallStatus;
          setActiveCall((prev) => {
            if (!prev) return null;
            return {
              ...prev,
              status: newStatus,
              result: callData.result || prev.result,
              durationSeconds:
                callData.duration_seconds > 0
                  ? callData.duration_seconds
                  : prev.durationSeconds,
            };
          });

          // Auto-fechamento após 6 segundos quando finalizada
          if (newStatus === "ENDED" || newStatus === "FAILED") {
            if (autoCloseTimeoutRef.current)
              clearTimeout(autoCloseTimeoutRef.current);
            autoCloseTimeoutRef.current = setTimeout(() => {
              setActiveCall(null);
            }, 6000);
          }
        }
      } catch {}
    };

    pollIntervalRef.current = setInterval(checkStatus, 1500);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCall?.id, activeCall?.status]);

  const startCall = useCallback(
    async ({
      dealId,
      phone,
      contactName,
      salesId,
    }: StartCallParams): Promise<boolean> => {
      if (!phone || !phone.trim()) {
        toast.error("O contato não possui número de telefone válido.");
        return false;
      }

      if (autoCloseTimeoutRef.current) {
        clearTimeout(autoCloseTimeoutRef.current);
        autoCloseTimeoutRef.current = null;
      }

      // Inicia estado de discagem
      const tempCallId = `temp_${Date.now()}`;
      setActiveCall({
        id: tempCallId,
        dealId,
        phone,
        contactName,
        status: "QUEUED",
        durationSeconds: 0,
        startedAt: Date.now(),
      });

      try {
        const supabase = getSupabaseClient();
        const {
          data: { session },
        } = await supabase.auth.getSession();
        const token = session?.access_token;

        const headers: Record<string, string> = {
          "Content-Type": "application/json",
        };
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        }

        const res = await fetch(`/api/leads/${dealId}/call`, {
          method: "POST",
          headers,
          credentials: "include",
          body: JSON.stringify({
            phone,
            contactName,
            sales_id: salesId,
          }),
        });

        const json = await res.json().catch(() => ({}));

        if (!res.ok || !json.success) {
          const errMsg =
            json?.error?.message ||
            json?.error ||
            "Não foi possível iniciar a chamada.";

          setActiveCall((prev) =>
            prev
              ? {
                  ...prev,
                  status: "FAILED",
                  errorMessage: errMsg,
                }
              : null,
          );

          toast.error(errMsg, { duration: 5000 });
          return false;
        }

        const callData = json.data;
        setActiveCall({
          id: callData.id,
          gatewayCallId: callData.gateway_call_id,
          dealId,
          phone: callData.phone || phone,
          contactName: callData.contactName || contactName,
          status: (callData.status as CallStatus) || "QUEUED",
          durationSeconds: 0,
          startedAt: Date.now(),
        });

        toast.success(
          `Chamada iniciada via WhatsApp Web (${callData.instanceId || "Dotus WA Call"}).`,
        );
        return true;
      } catch (err: any) {
        const errMsg = err?.message || "Erro de rede ao conectar com o CRM.";
        setActiveCall((prev) =>
          prev
            ? {
                ...prev,
                status: "FAILED",
                errorMessage: errMsg,
              }
            : null,
        );
        toast.error(errMsg);
        return false;
      }
    },
    [],
  );

  const dismissCall = useCallback(() => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    if (autoCloseTimeoutRef.current) clearTimeout(autoCloseTimeoutRef.current);
    setActiveCall(null);
  }, []);

  const isCalling = Boolean(
    activeCall &&
      activeCall.status !== "ENDED" &&
      activeCall.status !== "FAILED",
  );

  return (
    <DotusWaCallContext.Provider
      value={{
        activeCall,
        isCalling,
        startCall,
        dismissCall,
        currentUserInstanceId,
      }}
    >
      {children}
      {/* Barra Flutuante Global de Chamada Ativa */}
      {activeCall && (
        <DotusCallFloatingBar call={activeCall} onDismiss={dismissCall} />
      )}
    </DotusWaCallContext.Provider>
  );
};

function formatTimer(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins < 10 ? "0" : ""}${mins}:${secs < 10 ? "0" : ""}${secs}`;
}

interface FloatingBarProps {
  call: ActiveDotusCall;
  onDismiss: () => void;
}

const DotusCallFloatingBar: React.FC<FloatingBarProps> = ({
  call,
  onDismiss,
}) => {
  const getStatusDisplay = () => {
    switch (call.status) {
      case "QUEUED":
        return {
          title: "Conectando ao WhatsApp...",
          description: "Preparando a estação local do operador",
          icon: <Loader2 className="w-4 h-4 text-emerald-500 animate-spin" />,
          badgeBg: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
        };
      case "CALLING":
        return {
          title: "Discando...",
          description: `Chamando ${call.phone}`,
          icon: (
            <PhoneCall className="w-4 h-4 text-emerald-500 animate-pulse" />
          ),
          badgeBg: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
        };
      case "RINGING":
        return {
          title: "Chamando no telefone do cliente...",
          description: "Aguardando atendimento no WhatsApp",
          icon: <Volume2 className="w-4 h-4 text-emerald-500 animate-bounce" />,
          badgeBg: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
        };
      case "CONNECTED":
        return {
          title: "Em chamada conectada",
          description: "Conversa em andamento e gravando",
          icon: (
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
            </span>
          ),
          badgeBg: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30",
        };
      case "ENDED":
        return {
          title: "Chamada finalizada",
          description:
            call.result === "CONNECTED"
              ? `Duração total: ${formatTimer(call.durationSeconds)}`
              : `Resultado: ${call.result || "Sem resposta"}`,
          icon: <CheckCircle2 className="w-4 h-4 text-muted-foreground" />,
          badgeBg: "bg-muted text-muted-foreground border-border",
        };
      case "FAILED":
        return {
          title: "Falha na chamada",
          description:
            call.errorMessage || "Instância offline ou ocupada no momento",
          icon: <AlertCircle className="w-4 h-4 text-destructive" />,
          badgeBg: "bg-destructive/10 text-destructive border-destructive/20",
        };
      default:
        return {
          title: "Chamada em andamento",
          description: call.phone,
          icon: <Phone className="w-4 h-4 text-primary" />,
          badgeBg: "bg-primary/10 text-primary border-primary/20",
        };
    }
  };

  const statusInfo = getStatusDisplay();

  return (
    <div
      className="fixed bottom-6 right-6 z-50 max-w-sm w-full animate-in fade-in slide-in-from-bottom-5 duration-300"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="bg-card/95 backdrop-blur-md rounded-2xl border border-border shadow-2xl p-4 space-y-3 ring-1 ring-black/5 dark:ring-white/10">
        {/* Top Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
              <Phone className="w-3.5 h-3.5" />
            </div>
            <div>
              <span className="text-xs font-bold text-foreground tracking-tight">
                Dotus WA Call
              </span>
              <span className="text-[10px] text-muted-foreground block -mt-0.5">
                Telefonia WhatsApp Web
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {call.status === "CONNECTED" && (
              <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-600 dark:text-emerald-400 text-xs font-mono font-bold tracking-wider">
                <Clock className="w-3 h-3 animate-spin" />
                {formatTimer(call.durationSeconds)}
              </div>
            )}

            <Button
              variant="ghost"
              size="sm"
              onClick={onDismiss}
              className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground rounded-full"
              title="Fechar painel"
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        {/* Lead Target Info */}
        <div className="bg-muted/50 rounded-xl p-2.5 space-y-0.5 border border-border/50">
          <p className="text-xs font-semibold text-foreground truncate">
            {call.contactName || "Contato do Lead"}
          </p>
          <p className="text-[11px] font-mono text-muted-foreground">
            {call.phone}
          </p>
        </div>

        {/* Status Box */}
        <div
          className={`flex items-start gap-2.5 p-2.5 rounded-xl border text-xs ${statusInfo.badgeBg} transition-all`}
        >
          <div className="mt-0.5 shrink-0">{statusInfo.icon}</div>
          <div className="space-y-0.5 flex-1 min-w-0">
            <p className="font-semibold text-xs leading-none">
              {statusInfo.title}
            </p>
            <p className="text-[11px] opacity-85 leading-snug break-words">
              {statusInfo.description}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
